import React, { useEffect, useMemo, useState } from "react";
import { apiService } from "../../../api/apiService";

/**
 * WYBÓR SPÓŁKI / DZIAŁU dla formularza wniosku HR (pola `companyPicker` i `departmentPicker`).
 *
 * [PAKIET 2026-09-02, FUNDAMENT §4] Dlaczego osobny komponent, a nie zwykły `<select>` w rendererze:
 * `hrFormFieldRenderer` jest funkcją, nie komponentem — nie może mieć `useEffect`, więc nie pobierze
 * listy spółek ani działów. Zwykły select z pustą listą byłby polem, którego nie da się wypełnić,
 * czyli dokładnie tym, co fundament nazywa „warstwą bez wpięcia".
 *
 * WARTOŚĆ jest ZŁOŻONA — `{ id, name }`, tak samo jak przy wyborze osoby (`userPicker`). Powód: dokument
 * musi wydrukować NAZWĘ (identyfikator na piśmie prawnym to śmieć), a sprawa onboardingowa potrzebuje
 * identyfikatora. Trzymanie samego id oznaczałoby, że po skasowaniu działu z bazy stary wniosek przestaje
 * mówić, o jaki dział chodziło — a wniosek jest dokumentem archiwalnym i musi zostać czytelny.
 */
const OrgEntityPicker = ({
    kind,                 // "company" | "department"
    value,                // { id, name } albo samo id (zgodność wstecz) albo null
    onChange,
    disabled = false,
    required = false,
    className = "",
    errorClassName = "",
    hasError = false,
}) => {
    const [options, setOptions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [failed, setFailed] = useState(false);

    useEffect(() => {
        let anulowane = false;

        const pobierz = async () => {
            setLoading(true);
            setFailed(false);
            try {
                const res = kind === "company"
                    ? await apiService.getHrMyCompanies()
                    : await apiService.getAllDepartmentsSimple();
                const dane = Array.isArray(res?.data) ? res.data : (res?.data?.items ?? []);
                if (anulowane) return;
                setOptions(dane.map((d) => ({
                    id: d.id ?? d.companyId ?? d.departmentId,
                    name: d.name ?? d.label ?? d.companyName ?? d.departmentName ?? "",
                })).filter((o) => o.id !== undefined && o.id !== null && o.name));
            } catch {
                // Awaria listy NIE może wyczyścić tego, co już wybrano — pokazujemy komunikat i zostawiamy
                // dotychczasową wartość, żeby zapisany wcześniej wniosek nie stracił działu przy podglądzie.
                if (!anulowane) setFailed(true);
            } finally {
                if (!anulowane) setLoading(false);
            }
        };

        pobierz();
        return () => { anulowane = true; };
    }, [kind]);

    const wybraneId = useMemo(() => {
        if (value === null || value === undefined || value === "") return "";
        if (typeof value === "object") return String(value.id ?? "");
        return String(value);
    }, [value]);

    const handleChange = (e) => {
        const id = e.target.value;
        if (!id) {
            onChange(null);
            return;
        }
        const opcja = options.find((o) => String(o.id) === id);
        onChange(opcja ? { id: opcja.id, name: opcja.name } : { id, name: "" });
    };

    // Wartość spoza listy (dział wyłączony, spółka spoza zasięgu użytkownika) MUSI zostać widoczna —
    // inaczej otwarcie starego wniosku pokazywałoby „— wybierz —" i sugerowało, że pola nikt nie wypełnił.
    const brakujacaOpcja = wybraneId && !options.some((o) => String(o.id) === wybraneId)
        ? { id: wybraneId, name: (typeof value === "object" && value?.name) || "Pozycja spoza listy" }
        : null;

    return (
        <>
            <select
                className={`${className} ${hasError ? errorClassName : ""}`}
                value={wybraneId}
                onChange={handleChange}
                disabled={disabled || loading}
                required={required}
            >
                <option value="">
                    {loading
                        ? "Wczytywanie..."
                        : (kind === "company" ? "Wybierz spółkę..." : "Wybierz dział...")}
                </option>
                {brakujacaOpcja && (
                    <option key={brakujacaOpcja.id} value={brakujacaOpcja.id}>
                        {brakujacaOpcja.name}
                    </option>
                )}
                {options.map((o) => (
                    <option key={o.id} value={o.id}>{o.name}</option>
                ))}
            </select>
            {failed && (
                <small style={{ color: "var(--color-danger, #EF4444)" }}>
                    Nie udało się wczytać listy. Odśwież stronę — zapisany wybór pozostaje bez zmian.
                </small>
            )}
        </>
    );
};

export default OrgEntityPicker;
